// Libraries
import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
// Components
import UploadCsvModalStyles from 'components/admin/businesses/devices/UploadCsvModal.module.scss';
import { normalizeAndValidateMacAddress } from 'utils/formatters/macAddressFormatter';
// Api
import { uploadDevices } from 'api/admin/devices/devices';
import { useHandleBulkDeviceUpload } from 'api/queries/useHandleDeviceBulkUpload';
import { ReactComponent as CheckCircleIcon } from 'assets/images/icon_check_circle.svg';
import { ReactComponent as FileIcon } from 'assets/images/icon_file.svg';

export function UploadDeviceCsvModal({ businessId, onClose, refetchBatches, stores }) {
  const steps = {
    STORE_SELECTION: 'STORE_SELECTION',
    MANUAL_UPLOAD: 'MANUAL_UPLOAD',
    DEVICE_UPLOAD: 'DEVICE_UPLOAD',
  };

  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [isUploaded, setIsUploaded] = useState(false);
  const [step, setStep] = useState(steps.STORE_SELECTION);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [selectedStoreName, setSelectedStoreName] = useState(null);
  const [macAddresses, setMacAddresses] = useState([]);
  const [textAreaValue, setTextAreaValue] = useState('');
  const [invalidEntries, setInvalidEntries] = useState({});

  const handleDeviceUploadSuccess = () => {
    setIsUploaded(true);
    refetchBatches();
  };

  const {
    mutateAsync: handleBulkDeviceUpload,
    isLoading: isPerformingBulkDeviceUpload,
    data: createdDeviceList,
  } = useHandleBulkDeviceUpload({
    successCallback: () => {
      refetchBatches();
    },
  });

  const { mutate: uploadDevicesCsv, isLoading: isUploadingDevices } = useMutation({
    mutationFn: () => {
      const formData = new FormData();
      formData.append('businessId', String(businessId));
      formData.append('deviceList', file);

      if (selectedStoreId) {
        formData.append('storeId', String(selectedStoreId));
      }

      return uploadDevices(formData);
    },
    onSuccess: handleDeviceUploadSuccess,
    onError: (err) => {
      Sentry.captureException(err);
      setError(err.response.data.error);
    },
  });

  useEffect(() => {
    if (createdDeviceList?.successCount === macAddresses.length) {
      onClose();
    }
  }, [createdDeviceList, macAddresses.length, onClose]);

  const handleInputChange = (event) => {
    setFile(event.target.files[0]);
    setError(null);
  };

  const handleTextAreaChange = (event) => {
    const value = event.target.value;
    setTextAreaValue(value);

    const lines = value.split('\n');
    const newMacAddresses = [];
    const newInvalidEntries = new Set();

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      if (trimmedLine === '') {
        newMacAddresses.push('');
        return;
      }

      // add formatting
      const formattedMacAddressResult = normalizeAndValidateMacAddress(trimmedLine);

      if (formattedMacAddressResult.isValid) {
        newMacAddresses.push(formattedMacAddressResult.normalizedMacAddress);
      } else {
        newInvalidEntries.add(trimmedLine);

        // add the original input to the array so user can correct it
        newMacAddresses.push(trimmedLine);
      }

      setMacAddresses(newMacAddresses);
      setInvalidEntries(newInvalidEntries);
    });
  };

  const handleNormalizeAll = () => {
    const normalizedAddresses = macAddresses.map((macAddress) => {
      if (macAddress.trim() === '') {
        return '';
      }

      const result = normalizeAndValidateMacAddress(macAddress);
      return result.isValid ? result.normalizedMacAddress : macAddress;
    });

    setMacAddresses(normalizedAddresses);
    setTextAreaValue(normalizedAddresses.join('\n'));
  };

  const handleManualUpload = async () => {
    handleNormalizeAll();

    const uniqueMacAddresses = Array.from(new Set(macAddresses)).filter(
      (macAddress) => macAddress.trim() !== '',
    );

    setMacAddresses(uniqueMacAddresses);
    setTextAreaValue(uniqueMacAddresses.join('\n'));

    return handleBulkDeviceUpload({
      businessId,
      storeId: selectedStoreId,
      devices: uniqueMacAddresses,
    });
  };

  const getErrorMessagePositions = () => {
    if (isEmpty(createdDeviceList?.validationErrors)) {
      return [];
    }

    if (Object.keys(createdDeviceList?.validationErrors).length === 0) {
      return [];
    }

    const lines = textAreaValue.split('\n');
    const positions = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      if (trimmedLine) {
        const result = normalizeAndValidateMacAddress(trimmedLine);
        const normalizedMacAddress = result.isValid
          ? result.normalizedMacAddress
          : trimmedLine.toLowerCase();
        const foundError = createdDeviceList.validationErrors.find(
          (error) => error.deviceMacAddress === normalizedMacAddress,
        );

        if (foundError) {
          positions.push({
            message: foundError.errorMessage,
            macAddress: normalizedMacAddress,
            index,
          });
        }
      }
    });

    return positions;
  };

  const renderStoreSelection = () => {
    return (
      <>
        <DialogTitle>Select Store</DialogTitle>
        <DialogContent
          style={{
            width: '600px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Autocomplete
            id="autocomplete-choose-store"
            getOptionLabel={(option) => {
              return option.name ? option.name : option;
            }}
            options={stores}
            sx={{ width: '400px' }}
            onChange={(event, value) => {
              setSelectedStoreName(value.name);
              setSelectedStoreId(value.id);
            }}
            renderInput={(params) => <TextField {...params} label="Store" />}
            value={selectedStoreName}
            isOptionEqualToValue={(option, value) => option.name === value}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={() => setStep(steps.MANUAL_UPLOAD)}>
            Next
          </Button>
        </DialogActions>
      </>
    );
  };

  const errorPositions = getErrorMessagePositions();
  const totalLines = textAreaValue.split('\n').length;

  const renderManualInputForm = () => {
    return (
      <>
        <DialogContent style={{ width: '600px' }}>
          {isPerformingBulkDeviceUpload && (
            <div className={UploadCsvModalStyles.deviceUploadIndicator}>
              <CircularProgress />
            </div>
          )}
          {!isPerformingBulkDeviceUpload && (
            <div className={UploadCsvModalStyles.card}>
              <div className={UploadCsvModalStyles.cardHeader}>
                <div className={UploadCsvModalStyles.headerContent}>
                  <h2 className={UploadCsvModalStyles.cardTitle}>Upload Devices</h2>
                  <button
                    className={UploadCsvModalStyles.linkButton}
                    onClick={() => {
                      setStep(steps.DEVICE_UPLOAD);
                    }}
                  >
                    Upload by CSV
                  </button>
                </div>
              </div>

              <div className={UploadCsvModalStyles.cardContent}>
                <div className={UploadCsvModalStyles.textareaContainer}>
                  <textarea
                    value={textAreaValue}
                    onChange={handleTextAreaChange}
                    placeholder="Enter MAC addresses (one per line)"
                    className={UploadCsvModalStyles.macAddressTextarea}
                  />

                  {errorPositions.length > 0 && (
                    <div
                      className={UploadCsvModalStyles.errorOverlay}
                      style={{
                        gridTemplateRows: `repeat(${Math.max(totalLines, 10)}, 2rem)`, // ensure we have enough rows visible
                      }}
                    >
                      {errorPositions.map((error, index) => (
                        <div
                          key={`${error.macAddress}-${index}`}
                          className={UploadCsvModalStyles.errorLine}
                          style={{ gridRow: error.index + 1 }}
                        >
                          <span className={UploadCsvModalStyles.errorMessage}>{error.message}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="controls">
                  <div className="status">
                    {invalidEntries.size > 0 && (
                      <p className={UploadCsvModalStyles.errorText}>
                        {invalidEntries.size} invalid MAC address(es) detected
                      </p>
                    )}
                  </div>
                  <button
                    className={UploadCsvModalStyles.formatButton}
                    onClick={handleNormalizeAll}
                  >
                    Format All Addresses
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Button
            onClick={() => {
              setStep(steps.STORE_SELECTION);
            }}
          >
            Back
          </Button>
          <Button onClick={onClose}>Close</Button>
          <Button disabled={invalidEntries.size > 0 || macAddresses.length === 0} variant="contained" onClick={handleManualUpload}>
            Upload
          </Button>
        </DialogActions>
      </>
    );
  };

  const renderDeviceUpload = () => {
    return (
      <>
        <DialogTitle>Upload CSV</DialogTitle>
        <DialogContent>
          {isUploadingDevices && (
            <div className={UploadCsvModalStyles.deviceUploadIndicator}>
              <CircularProgress />
            </div>
          )}
          {!isUploadingDevices && !isUploaded && (
            <>
              <div className={UploadCsvModalStyles.uploadAction}>
                <form>
                  <input
                    type="file"
                    accept="text/csv"
                    className={UploadCsvModalStyles.inputFile}
                    id="input-file"
                    onChange={handleInputChange}
                  />
                  <label htmlFor="input-file" className={UploadCsvModalStyles.inputFileLabel}>
                    Browse files to upload
                  </label>
                </form>
              </div>
              {file && !error && (
                <div>
                  <FileIcon />
                  <span>{file.name}</span>
                </div>
              )}
              {error && (
                <div>
                  <span style={{ color: 'red' }}>{error}</span>
                </div>
              )}
            </>
          )}
          {isUploaded && (
            <div className={UploadCsvModalStyles.deviceUploadSuccess}>
              <CheckCircleIcon className={UploadCsvModalStyles.iconSuccess} />
              <div>File uploaded successfully</div>
            </div>
          )}
        </DialogContent>
        {isUploaded && (
          <DialogActions>
            <Button variant="contained" onClick={onClose}>
              OK
            </Button>
          </DialogActions>
        )}
        {!isUploaded && (
          <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={() => setStep(steps.STORE_SELECTION)}>Back</Button>
            <Button disabled={!file} variant="contained" onClick={uploadDevicesCsv}>
              Upload
            </Button>
          </DialogActions>
        )}
      </>
    );
  };

  const renderContent = () => {
    switch (step) {
      case steps.STORE_SELECTION:
        return renderStoreSelection();
      case steps.MANUAL_UPLOAD:
        return renderManualInputForm();
      case steps.DEVICE_UPLOAD:
        return renderDeviceUpload();
      default:
        return renderManualInputForm();
    }
  };

  return (
    <Dialog open={true} onClose={onClose}>
      {renderContent()}
    </Dialog>
  );
}

UploadDeviceCsvModal.propTypes = {
  businessId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  refetchBatches: PropTypes.func.isRequired,
  stores: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ).isRequired,
};
